"use client";

import React, { type FC } from "react";
import { Body2, ButtonText, H3 } from "./Text";
import { Button } from "@/components/ui/adapters";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { AlertCircle, RefreshCw, NotepadText } from "lucide-react";

// Props for Sentry ErrorBoundary fallback function
// Ref: https://docs.sentry.io/platforms/javascript/guides/react/features/error-boundary/
interface ErrorFallbackProps {
  className?: string;
}

const ErrorFallback: FC<ErrorFallbackProps> = ({ className }) => {
  const t = useTranslations();
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const handleRefresh = (): void => {
    setIsRefreshing(true);
    window.location.reload();
  };

  const handleContactUs = (): void => {
    window.open("https://contact.sowaka.io/", "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={clsx(
        "flex flex-col items-center justify-center min-h-dvh xl:min-h-screen w-full px-4 relative overflow-hidden",
        className
      )}
    >
      {/* Background */}
      <div className="absolute inset-0 bg-white" />

      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-linear-to-br from-white via-gray-50/30 to-white" />

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, currentColor 1px, transparent 1px),
              linear-gradient(to bottom, currentColor 1px, transparent 1px)
            `,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      {/* Dots pattern */}
      <div className="absolute inset-0 opacity-[0.04]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
            backgroundSize: "32px 32px",
          }}
        />
      </div>

      {/* Decorative lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-linear-to-r from-transparent via-gray-200 to-transparent opacity-20" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-linear-to-r from-transparent via-gray-200 to-transparent opacity-20" />

      {/* Corner decorations */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-linear-to-br from-blue-50/40 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-linear-to-tl from-pink-50/40 to-transparent rounded-full blur-3xl" />

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center max-w-md w-full animate-in fade-in duration-500">
        {/* Icon container with animation */}
        <div className="relative mb-6">
          <div className="absolute inset-0 bg-red-100 rounded-full blur-2xl animate-pulse" />
          <div className="relative bg-white rounded-full p-4 shadow-lg border-2 border-red-100">
            <AlertCircle className="w-16 h-16 text-red-500 animate-in zoom-in duration-500" />
          </div>
        </div>

        {/* Title */}
        <H3 className="text-center mb-3">{t("something_went_wrong")}</H3>

        {/* Description */}
        <Body2 className="text-center mb-6 max-w-sm">
          {t("web.unexpected_error")}
        </Body2>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Refresh Page button */}
          <Button
            color="primary"
            variant="solid"
            className="min-w-[140px] h-12 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
            onPress={handleRefresh}
            isLoading={isRefreshing}
            startContent={!isRefreshing && <RefreshCw className="w-4 h-4" />}
          >
            <ButtonText>{t("web.refresh_page") || "Refresh Page"}</ButtonText>
          </Button>

          {/* Contact Us button */}
          <Button
            color="primary"
            variant="bordered"
            className="min-w-[140px] h-12 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
            onPress={handleContactUs}
            startContent={<NotepadText className="w-4 h-4" />}
          >
            <ButtonText>{t("web.contact_us") || "Contact Us"}</ButtonText>
          </Button>
        </div>

        {/* Decorative gradient circles */}
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-linear-to-br from-orange-100 to-orange-200 rounded-full blur-3xl -z-10 opacity-60" />
      </div>
    </div>
  );
};

export default ErrorFallback;
